import { useState, useRef } from 'react';
import moment from 'moment';
import { TriggerButton } from 'ui/menu/Styled';
import { DateRangePicker } from 'ui/datePicker';
import { useClickAway } from 'react-use';
import { getNewDate, isDateInRange } from 'utils/date';
import { RELATIVE_DATE_PRESETS } from 'ui/datePicker/helpers';
import { Tooltip } from 'ui/tooltip';
import { BasicSelect, RAW_SELECT_DATA_PORTAL_ID } from 'ui/selects';
import {
  StyledChevronDown,
  StyledBanner,
  DatePickerContainer,
  DatePickerWrapper,
  DateBarTimeContainer,
} from './Styled';
import { MDYTimeFormat } from '../constants';
import { useFiltersContext, useTimeFiltersContext } from '../context';

export const DateBar = ({ campaignStartTime, campaignEndTime, campaign, variantOptions }) => {
  const { dateParams, setDateParams, timezone, setTimezone, timePreset, setTimePreset } =
    useTimeFiltersContext();
  const { variantId, setVariantId } = useFiltersContext();

  const campaignStart = getNewDate({ date: campaignStartTime || campaign?.createdAt, timezone });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const RELATIVE_DATES_WITH_CAMPAIGN_LENGTH = {
    ...RELATIVE_DATE_PRESETS,
    ENTIRE_CAMPAIGN: {
      menuLabel: 'Campaign start',
      filterLabel: 'from the start of the campaign',
      format: (timeZone) => ({
        startDate: getNewDate({ date: campaign?.createdAt, timeZone }),
        endDate: getNewDate({ date: new Date(), timeZone }),
      }),
    },
  };

  const getTimeText = (start, end, preset) => {
    if (preset === 'CUSTOM_RANGE') {
      return `between ${start.format('MM/DD/YY')} - ${end.format('MM/DD/YY')}`;
    }
    return RELATIVE_DATES_WITH_CAMPAIGN_LENGTH[preset].filterLabel;
  };
  const wrapperRef = useRef(null);
  useClickAway(wrapperRef, () => {
    const timeZonePickerDropdownPortal = document.querySelector(
      `[data-portal-id="${RAW_SELECT_DATA_PORTAL_ID}"]`,
    ); // NOTE: only one of these should exist at a time

    /**
     * If the click originated from the time zone dropdown(i.e. the dropdown is open),
     * don't close the date picker. The dropdown portal must be close before we start acknowledging
     * clicks outside of the date picker.
     */
    if (timeZonePickerDropdownPortal) {
      return;
    }
    setShowDatePicker(false);
  });
  return (
    <StyledBanner>
      {dateParams && (
        <div>
          Viewing metrics recorded{' '}
          <DatePickerContainer data-test={'date-picker'}>
            <TriggerButton onClick={() => setShowDatePicker(!showDatePicker)}>
              {getTimeText(dateParams.startTime, dateParams.endTime, timePreset)}
              <StyledChevronDown height={18} />
            </TriggerButton>
            {showDatePicker && (
              <DatePickerWrapper ref={wrapperRef}>
                <DateRangePicker
                  startDate={dateParams.startTime}
                  endDate={dateParams.endTime}
                  dateRangePreset={timePreset}
                  validDateRange={{ start: campaignStart }}
                  onDateRangeChange={({ startDate, endDate }) => {
                    setDateParams({ startTime: startDate, endTime: endDate });
                  }}
                  onPresetChange={(preset) => setTimePreset(preset)}
                  onTimeZoneChange={(tz) => setTimezone(tz)}
                  defaultTimeZone={timezone}
                  presets={RELATIVE_DATES_WITH_CAMPAIGN_LENGTH}
                  promoteCustomInput
                  blockFutureDates
                  customCalendarTileContent={({ date }) => {
                    return (
                      <div data-test="calendar-tile-date">
                        <Tooltip
                          isEnabled={!isDateInRange(moment(date), campaignStart)}
                          tooltipContent={`Your campaign wasn't launched yet on this date. Please select a date range that begins on ${campaignStart.format(
                            'MMM DD YYYY',
                          )} or later.`}
                        >
                          {moment(date).format('D')}
                        </Tooltip>
                      </div>
                    );
                  }}
                />
              </DatePickerWrapper>
            )}
          </DatePickerContainer>
          {variantOptions.length > 1 && (
            <>
              {' '}
              for{' '}
              <BasicSelect
                defaultValues={[variantId]}
                onSelect={(val) => setVariantId(val[0])}
                options={variantOptions}
              />
            </>
          )}
        </div>
      )}

      {campaignStartTime && campaignEndTime && (
        <DateBarTimeContainer>
          <div>Launched {moment(campaignStartTime).format(MDYTimeFormat)}</div>
          <div>Ended {moment(campaignEndTime).format(MDYTimeFormat)}</div>
        </DateBarTimeContainer>
      )}
    </StyledBanner>
  );
};
